import type {RecurringCadence} from '../../../../../../generated/enums.ts';

export const CADENCE_LABELS: Record<RecurringCadence, string> = {
    weekly: 'Wekelijks',
    monthly: 'Maandelijks',
    quarterly: 'Per kwartaal',
    yearly: 'Jaarlijks',
}

export const CADENCE_DESCRIPTIONS: Record<RecurringCadence, string> = {
    weekly: 'elke week',
    monthly: 'elke maand',
    quarterly: 'elke 3 maanden',
    yearly: 'elk jaar',
}

export const CADENCE_ORDER: RecurringCadence[] = [
    'weekly',
    'monthly',
    'quarterly',
    'yearly',
]

const CADENCE_MONTHS: Record<Exclude<RecurringCadence, 'weekly'>, number> = {
    monthly: 1,
    quarterly: 3,
    yearly: 12,
}

function isRecurringCadence(value: string): value is RecurringCadence {
    return value in CADENCE_LABELS
}

export function cadenceLabel(cadence: string): string {
    return isRecurringCadence(cadence) ? CADENCE_LABELS[cadence] : cadence
}

export function cadenceDescription(cadence: string): string {
    return isRecurringCadence(cadence) ? CADENCE_DESCRIPTIONS[cadence] : `elke ${cadence}`
}

export function formatDateNL(isoDate: string | null): string {
    if (!isoDate) return '-'
    const [year, month, day] = isoDate.slice(0, 10).split('-')
    if (!year || !month || !day) return '-'
    return `${day}-${month}-${year}`
}

export function toIsoDate(date: Date): string {
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    return `${date.getFullYear()}-${month}-${day}`
}

function parseIsoDate(isoDate: string): { year: number, month: number, day: number } {
    const [year, month, day] = isoDate.slice(0, 10).split('-').map(Number)
    return {year, month, day}
}

export function occurrence(anchorIso: string, index: number, cadence: RecurringCadence): string {
    const {year, month, day} = parseIsoDate(anchorIso)

    if (cadence === 'weekly') {
        return toIsoDate(new Date(year, month - 1, day + index * 7))
    }

    const monthsAhead = month - 1 + index * CADENCE_MONTHS[cadence]
    const lastDayOfTargetMonth = new Date(year, monthsAhead + 1, 0).getDate()
    return toIsoDate(new Date(year, monthsAhead, Math.min(day, lastDayOfTargetMonth)))
}

export function firstOccurrenceOnOrAfter(
    anchorIso: string,
    earliestIso: string,
    cadence: RecurringCadence,
): string {
    let index = 0
    let candidate = occurrence(anchorIso, index, cadence)
    while (candidate < earliestIso) {
        index += 1
        candidate = occurrence(anchorIso, index, cadence)
    }
    return candidate
}

export function firstOccurrenceAfter(
    anchorIso: string,
    dayIso: string,
    cadence: RecurringCadence,
): string {
    let index = 0
    let candidate = occurrence(anchorIso, index, cadence)
    while (candidate <= dayIso) {
        index += 1
        candidate = occurrence(anchorIso, index, cadence)
    }
    return candidate
}

export function nextOccurrenceAfterInvoiceDate(
    invoiceDateIso: string,
    cadence: RecurringCadence,
): string {
    return firstOccurrenceAfter(invoiceDateIso, invoiceDateIso, cadence)
}

export function addDaysToIsoDate(isoDate: string, days: number): string {
    const {year, month, day} = parseIsoDate(isoDate)
    return toIsoDate(new Date(year, month - 1, day + days))
}
